/**
 * WP-V2-VET — Weterynarz „Pieskowo Weterynarz".
 * Darmowa opieka: osłuchanie, bandaż, temperatura — bez zakupów.
 * Wizualia: proceduralne tekstury (bez emoji-obiektów).
 */
import Phaser from 'phaser';
import { BaseScene } from '../../core/BaseScene.js';
import { worldBackButton } from '../../core/worldBackButton.js';
import {
  AnimatedDogAvatar,
  OutfitStore,
  preloadAnchors,
} from '../../avatar/index.js';
import { playFx } from '../../interact/index.js';
import {
  setupWorld,
  follow,
  WalkController,
} from '../../core/world/index.js';
import {
  EXAM_APPROACH,
  EXAM_TABLE,
  MED_SHELF,
  PLAYER_START,
  RECEPTION_POS,
  SHELF_APPROACH,
  VET_CARE_SPOTS,
  VET_COLLIDERS,
  VET_WAITING_CHAIRS,
  VET_WORLD_H,
  VET_WORLD_W,
  ensureVetTextures,
} from './objects.js';
import {
  VET_CARE_ACTIONS,
  VET_DECO_TEXTURES,
  VET_HEALTHY_MESSAGE_PL,
  careById,
  emptyVisitState,
  isCareDone,
  performCare,
  progressLabelPl,
  type VetCareId,
  type VetVisitState,
} from './vetLogic.js';

export class VetScene extends BaseScene {
  static readonly KEY = 'Vet';

  private avatar: AnimatedDogAvatar | null = null;
  private walk: WalkController | null = null;
  private visit: VetVisitState = emptyVisitState();
  private toast: Phaser.GameObjects.Text | null = null;
  private statusText: Phaser.GameObjects.Text | null = null;
  private progressText: Phaser.GameObjects.Text | null = null;
  private careButtons: Map<VetCareId, Phaser.GameObjects.Container> = new Map();
  private busy = false;

  constructor() {
    super(VetScene.KEY);
  }

  preload(): void {
    if (!this.textures.exists('bldg_vet')) {
      this.load.image('bldg_vet', '/assets/real/bldg_vet.png');
    }
    for (const pose of ['idle', 'walkA', 'walkB', 'sit', 'jump', 'wave'] as const) {
      const key = `dog_maltese_${pose}`;
      if (!this.textures.exists(key)) {
        this.load.image(key, `/assets/real/maltese_${pose}.png`);
      }
    }
    if (!this.textures.exists('ph_dog')) {
      this.load.image('ph_dog', '/assets/placeholder/dog.png');
    }
    void preloadAnchors(this);
  }

  create(): void {
    const cache = OutfitStore.load();
    this.visit = emptyVisitState();
    this.busy = false;

    setupWorld(this, {
      worldW: VET_WORLD_W,
      worldH: VET_WORLD_H,
      bg: 0xe3f2fd,
    });

    ensureVetTextures(this);
    this.drawInterior();
    this.spawnPlayer(cache.breed, cache.color);
    this.buildChrome();
    this.buildCareHotspots();
    this.spawnReceptionHotspot();
    this.spawnChairHotspots();
    this.spawnPlantHotspot();
    this.spawnBackButton();

    this.events.once('shutdown', () => this.teardown());
    this.registry.set('companionFollow', {
      x: VET_WORLD_W / 2,
      y: 440,
    });
  }

  update(_t: number, dtMs: number): void {
    const dt = Math.min(0.05, dtMs / 1000);
    this.walk?.update(dt);
    const pos = this.walk?.position;
    this.avatar?.update(dt);
    if (pos && this.avatar) {
      this.avatar.container.setPosition(pos.x, pos.y);
    }
  }

  private drawInterior(): void {
    // ściany / podłoga — czysto, Toca, bez wtopionych napisów
    this.add
      .rectangle(
        VET_WORLD_W / 2,
        VET_WORLD_H / 2,
        VET_WORLD_W,
        VET_WORLD_H,
        0xe8f5e9,
      )
      .setDepth(-100);
    this.add
      .rectangle(VET_WORLD_W / 2, 620, VET_WORLD_W, 220, 0xcfeaf2)
      .setDepth(-90);
    this.add
      .rectangle(VET_WORLD_W / 2, 100, VET_WORLD_W, 70, 0xb3e5fc, 0.5)
      .setDepth(-80);

    if (this.textures.exists('bldg_vet')) {
      this.add
        .image(160, 130, 'bldg_vet')
        .setDisplaySize(150, 150)
        .setAlpha(0.9)
        .setDepth(-70);
    }

    // recepcja
    this.add
      .rectangle(310, 220, 240, 56, 0x90caf9)
      .setStrokeStyle(3, 0xffffff)
      .setDepth(4);
    this.add
      .circle(RECEPTION_POS.x, RECEPTION_POS.y, 32, 0xfff8f0)
      .setStrokeStyle(3, 0x81d4fa)
      .setDepth(8);

    // asystent — prawdziwa tekstura psa (nie emoji); klik w spawnReceptionHotspot
    const patientKey = this.textures.exists('dog_maltese_idle')
      ? 'dog_maltese_idle'
      : this.textures.exists('ph_dog')
        ? 'ph_dog'
        : null;
    if (patientKey) {
      this.add
        .image(RECEPTION_POS.x, RECEPTION_POS.y - 4, patientKey)
        .setDisplaySize(56, 56)
        .setDepth(9);
    } else {
      this.add.circle(RECEPTION_POS.x, RECEPTION_POS.y - 4, 24, 0xffe0b2).setDepth(9);
    }
    // stetoskop przy recepcji (proceduralny)
    this.add
      .image(RECEPTION_POS.x, RECEPTION_POS.y - 42, 'vet_tool_stethoscope')
      .setDisplaySize(36, 36)
      .setDepth(10);

    // stół do badania
    this.add
      .rectangle(EXAM_TABLE.x, EXAM_TABLE.y, EXAM_TABLE.w, EXAM_TABLE.h, 0xe1f5fe)
      .setStrokeStyle(4, 0xffffff)
      .setDepth(5);
    this.add
      .rectangle(EXAM_TABLE.x, EXAM_TABLE.y - 20, EXAM_TABLE.w - 40, 24, 0xffffff, 0.7)
      .setDepth(6);

    // półka z lekami / bandażami
    this.add
      .rectangle(MED_SHELF.x, MED_SHELF.y, MED_SHELF.w, MED_SHELF.h, 0xfff3e0)
      .setStrokeStyle(3, 0xffffff)
      .setDepth(5);
    for (let i = 0; i < 4; i++) {
      const cx = MED_SHELF.x - 60 + i * 40;
      this.add
        .rectangle(cx, MED_SHELF.y - 30, 22, 36, [0xef9a9a, 0x80cbc4, 0xffe082, 0xce93d8][i]!)
        .setStrokeStyle(2, 0xffffff)
        .setDepth(6);
    }
    this.add
      .image(MED_SHELF.x - 28, MED_SHELF.y + 40, 'vet_tool_bandage')
      .setDisplaySize(40, 40)
      .setDepth(7);
    this.add
      .image(MED_SHELF.x + 28, MED_SHELF.y + 40, VET_DECO_TEXTURES.medicine)
      .setDisplaySize(40, 40)
      .setDepth(7);

    // poczekalnia — krzesełka (klikalne w spawnChairHotspots)
    for (const c of VET_WAITING_CHAIRS) {
      this.add
        .rectangle(c.x, c.y, 70, 28, 0x81d4fa)
        .setStrokeStyle(2, 0xffffff)
        .setDepth(4);
      this.add
        .rectangle(c.x, c.y - 22, 64, 36, 0xb3e5fc)
        .setStrokeStyle(2, 0xffffff)
        .setDepth(4);
    }

    // roślina dekor — proceduralna (klikalna w spawnPlantHotspot)
    this.add
      .ellipse(1480, 520, 50, 28, 0xa5d6a7)
      .setStrokeStyle(2, 0xffffff)
      .setDepth(3);
    this.add
      .image(1480, 480, VET_DECO_TEXTURES.plant)
      .setDisplaySize(64, 64)
      .setDepth(4);
  }

  private spawnPlayer(breed: string, color: string): void {
    const cache = OutfitStore.load();
    this.avatar = new AnimatedDogAvatar({
      scene: this,
      x: PLAYER_START.x,
      y: PLAYER_START.y,
      breed: breed || cache.breed || 'maltese',
      color: color || cache.color || '#FFF5EE',
      slots: cache.slots,
      swimSlots: cache.swimSlots,
      depth: 100,
    });

    this.walk = new WalkController(this, this.avatar, {
      colliders: VET_COLLIDERS,
      bounds: {
        minX: 48,
        minY: 140,
        maxX: VET_WORLD_W - 48,
        maxY: VET_WORLD_H - 48,
      },
      enableInput: true,
    });

    follow(this, this.avatar.container, {
      lerp: 0.12,
      deadzone: { width: 100, height: 70 },
    });
  }

  private buildChrome(): void {
    this.add
      .text(VET_WORLD_W / 2, 36, 'Pieskowo Weterynarz', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '34px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_000);

    this.add
      .text(VET_WORLD_W / 2, 74, 'Zadbaj o pieska — 3 czynności opieki', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#6b5f7a',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_000);

    this.progressText = this.add
      .text(VET_WORLD_W / 2, 108, progressLabelPl(this.visit), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#1565c0',
        fontStyle: 'bold',
        backgroundColor: '#ffffffcc',
        padding: { x: 10, y: 4 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_000);

    this.statusText = this.add
      .text(VET_WORLD_W / 2, 148, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#4a3f55',
        backgroundColor: '#e3f2fd',
        padding: { x: 12, y: 6 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_000)
      .setVisible(false);

    this.toast = this.add
      .text(VET_WORLD_W / 2, 190, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        color: '#4a3f55',
        fontStyle: 'bold',
        backgroundColor: '#fff8f0',
        padding: { x: 14, y: 8 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_050)
      .setVisible(false);
  }

  private buildCareHotspots(): void {
    // przyciski HUD na dole + hotspoty na mapie
    const baseY = VET_WORLD_H - 70;
    const startX =
      VET_WORLD_W / 2 - ((VET_CARE_ACTIONS.length - 1) * 160) / 2;

    VET_CARE_ACTIONS.forEach((a, i) => {
      const x = startX + i * 160;
      const bg = this.add
        .rectangle(0, 0, 140, 88, a.color, 0.95)
        .setStrokeStyle(3, 0xffffff);
      const icon = this.add
        .image(0, -16, a.textureKey)
        .setDisplaySize(40, 40);
      const name = this.add
        .text(0, 22, a.namePl, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      const c = this.add.container(x, baseY, [bg, icon, name]);
      c.setSize(140, 88);
      c.setScrollFactor(0);
      c.setDepth(20_100);
      c.setInteractive(
        new Phaser.Geom.Rectangle(-70, -44, 140, 88),
        Phaser.Geom.Rectangle.Contains,
      );
      c.on('pointerdown', () => void this.onCare(a.id));
      c.setName(`hotspot-care-${a.id}`);
      this.careButtons.set(a.id, c);
    });

    // klikalne miejsca w pokoju
    for (const spot of VET_CARE_SPOTS) {
      const action = careById(spot.careId);
      if (!action) continue;
      const hit = this.add
        .circle(spot.x, spot.y, 44, action.color, 0.35)
        .setStrokeStyle(3, 0xffffff)
        .setDepth(40)
        .setInteractive({
          useHandCursor: true,
          hitArea: new Phaser.Geom.Circle(0, 0, 48),
          hitAreaCallback: Phaser.Geom.Circle.Contains,
        });
      this.add
        .image(spot.x, spot.y, action.textureKey)
        .setDisplaySize(40, 40)
        .setDepth(41);
      hit.on('pointerdown', () => void this.onCare(spot.careId));
      hit.setName(`hotspot-world-${spot.careId}`);
    }
  }

  /** Asystent przy recepcji — powitanie (był martwą dekoracją). */
  private spawnReceptionHotspot(): void {
    const hit = this.add
      .circle(RECEPTION_POS.x, RECEPTION_POS.y, 48, 0xffffff, 0.001)
      .setDepth(50)
      .setInteractive({
        useHandCursor: true,
        hitArea: new Phaser.Geom.Circle(0, 0, 52),
        hitAreaCallback: Phaser.Geom.Circle.Contains,
      });
    hit.on('pointerdown', () => {
      this.walk?.moveTo(320, 280);
      playFx(this, 'hearts', RECEPTION_POS.x, RECEPTION_POS.y - 30);
      this.showToast('Witaj! Zadbamy o pieska 🩺');
      void this.avatar?.playAction('wave');
    });
    hit.setName('hotspot-reception');
  }

  /** Krzesła poczekalni — siadanie. */
  private spawnChairHotspots(): void {
    for (const c of VET_WAITING_CHAIRS) {
      const hit = this.add
        .rectangle(c.x, c.y - 8, 80, 70, 0xffffff, 0.001)
        .setDepth(50)
        .setInteractive({ useHandCursor: true });
      hit.on('pointerdown', () => {
        this.walk?.moveTo(c.approachX, c.approachY);
        playFx(this, 'hearts', c.x, c.y - 20);
        this.showToast('Poczekalnia — siadamy!');
        void this.avatar?.playAction('sit');
      });
      hit.setName(`hotspot-${c.id}`);
    }
  }

  /** Roślina dekoracyjna — wąchanie. */
  private spawnPlantHotspot(): void {
    const hit = this.add
      .circle(1480, 490, 40, 0xffffff, 0.001)
      .setDepth(50)
      .setInteractive({
        useHandCursor: true,
        hitArea: new Phaser.Geom.Circle(0, 0, 44),
        hitAreaCallback: Phaser.Geom.Circle.Contains,
      });
    hit.on('pointerdown', () => {
      this.walk?.moveTo(1420, 520);
      playFx(this, 'stars', 1480, 470);
      this.showToast('Pachnie jak łąka!');
      void this.avatar?.playAction('happy');
    });
    hit.setName('hotspot-plant');
  }

  private spawnBackButton(): void {
    worldBackButton(this);
  }

  private async onCare(careId: VetCareId): Promise<void> {
    if (this.busy) return;
    this.busy = true;

    const approach =
      careId === 'bandage' ? SHELF_APPROACH : EXAM_APPROACH;
    this.walk?.moveTo(approach.x, approach.y);

    const result = performCare(this.visit, careId);
    this.visit = result.state;

    this.progressText?.setText(progressLabelPl(this.visit));
    this.setStatus(result.statusPl);
    this.showToast(result.statusPl);
    this.refreshCareButtonStyles();

    const pos = this.walk?.position ?? approach;
    if (careId === 'stethoscope') {
      playFx(this, 'notes', pos.x, pos.y - 30);
    } else if (careId === 'bandage') {
      playFx(this, 'sparkle', pos.x, pos.y - 20);
    } else {
      playFx(this, 'stars', pos.x, pos.y - 30);
    }

    if (this.avatar) {
      if (careId === 'stethoscope') {
        await this.avatar.playAction('sit');
      } else if (careId === 'bandage') {
        await this.avatar.playAction('happy');
      } else {
        await this.avatar.playAction('wave');
      }
      await this.avatar.playAction('happy');
    }

    if (result.justCompletedAll) {
      playFx(this, 'hearts', pos.x, pos.y - 40);
      this.showToast(VET_HEALTHY_MESSAGE_PL);
      this.setStatus(VET_HEALTHY_MESSAGE_PL);
      if (this.avatar) {
        await this.avatar.playAction('jump');
      }
    } else if (!result.alreadyDone) {
      playFx(this, 'hearts', pos.x, pos.y - 36);
    }

    this.busy = false;
  }

  private refreshCareButtonStyles(): void {
    for (const a of VET_CARE_ACTIONS) {
      const c = this.careButtons.get(a.id);
      if (!c) continue;
      const bg = c.list[0] as Phaser.GameObjects.Rectangle | undefined;
      if (!bg || !('setFillStyle' in bg)) continue;
      if (isCareDone(this.visit, a.id)) {
        bg.setFillStyle(0xc8e6c9, 1);
        bg.setStrokeStyle(4, 0xffd700);
      }
    }
  }

  private setStatus(msg: string): void {
    if (!this.statusText) return;
    this.statusText.setText(msg);
    this.statusText.setVisible(true);
  }

  private showToast(msg: string): void {
    if (!this.toast) return;
    this.toast.setText(msg);
    this.toast.setVisible(true);
    this.time.delayedCall(2400, () => this.toast?.setVisible(false));
  }

  private teardown(): void {
    this.walk?.destroy();
    this.walk = null;
    this.avatar?.destroy();
    this.avatar = null;
    this.careButtons.clear();
    this.registry.remove('companionFollow');
  }
}

export default VetScene;
